import { io, type Socket } from 'socket.io-client'
import type { NodeObject, AccessTokenResp, FullVehicleStatus2 } from '@/types/bluelink'

type AckCallback = (data: NodeObject) => void
type StreamCallback = (data: NodeObject) => void

interface PendingRequest {
  resolve: (value: NodeObject) => void
  reject: (reason: NodeObject | Error) => void
  onAck?: AckCallback
}

const SOCKET_URL = 'wss://napi.digify.no'
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000
const envTimeout = Number(import.meta.env.VITE_SOCKET_REQUEST_TIMEOUT_MS)
const REQUEST_TIMEOUT_MS = Number.isFinite(envTimeout) && envTimeout > 0
  ? envTimeout
  : DEFAULT_REQUEST_TIMEOUT_MS

class SocketService {
  private socket: Socket | null = null
  private pending = new Map<string, PendingRequest>()
  private streams = new Map<string, StreamCallback[]>()
  private reconnectHandlers = new Set<() => void>()
  private hasConnectedOnce = false
  private _sessionEstablished = false
  private sessionReadyQueue: Array<() => void> = []

  onceSessionReady(fn: () => void): void {
    if (this._sessionEstablished) {
      fn()
    } else {
      this.sessionReadyQueue.push(fn)
    }
  }

  private markSessionReady(): void {
    this._sessionEstablished = true
    const queue = this.sessionReadyQueue.splice(0)
    queue.forEach((fn) => fn())
  }

  onReconnect(handler: () => void): () => void {
    this.reconnectHandlers.add(handler)
    return () => this.reconnectHandlers.delete(handler)
  }

  connect(): void {
    if (this.socket) return

    this.socket = io(SOCKET_URL, { transports: ['websocket'] })
    this.socket.on('connect', () => {
      if (import.meta.env.DEV) console.log(this.hasConnectedOnce ? '[socket] reconnected' : '[socket] connected')
      void this.handleConnect()
      if (this.hasConnectedOnce) {
        this.reconnectHandlers.forEach((h) => h())
      }
      this.hasConnectedOnce = true
    })
    this.socket.on('connect_error', (err: Error) => {
      if (import.meta.env.DEV) console.error('[socket] connect_error', err)
      const error = new Error('Cannot connect to server. Check your network connection and try again.')
      for (const pending of this.pending.values()) {
        pending.reject(error)
      }
      this.pending.clear()
    })
    this.socket.on('disconnect', (reason: string) => {
      if (import.meta.env.DEV) console.log('[socket] disconnected:', reason)
      this._sessionEstablished = false
      this.sessionReadyQueue = []
      const error = new Error('Connection lost. Please try again.')
      for (const pending of this.pending.values()) {
        pending.reject(error)
      }
      this.pending.clear()
    })
    this.socket.on('NewObject', (data: NodeObject) => this.handleMessage(data))
  }

  private async handleConnect(): Promise<void> {
    const { useAuthStore } = await import('@/stores/auth')
    const auth = useAuthStore()
    if (auth.accessToken) {
      this.sendRaw({
        Event: 'AccessToken',
        Guid: crypto.randomUUID(),
        SubmitType: 'Request',
        AccessToken: {
          AccessToken: auth.accessToken,
          LatestChatMsg: new Date().toISOString(),
          SessionID: localStorage.getItem('insightssessionid') ?? crypto.randomUUID(),
        },
      })
    }
  }

  private handleMessage(data: NodeObject): void {
    if (import.meta.env.DEV) console.log('[socket] ←', data.Event, data.Guid, data.SubmitType)
    const { Guid, SubmitType } = data

    if (SubmitType === 'Stream') {
      const handlers = this.streams.get(Guid) ?? []
      handlers.forEach((cb) => cb(data))
      return
    }

    // AccessTokenResp from reconnect (no pending = not from an explicit request call)
    if (data.Event === 'AccessToken' && SubmitType === 'Response' && !this.pending.has(Guid)) {
      void this.handleReconnectAccessTokenResp(data as AccessTokenResp)
      return
    }

    const pending = this.pending.get(Guid)

    if (SubmitType === 'Ack') {
      pending?.onAck?.(data)
      return
    }

    if (!pending) return

    this.pending.delete(Guid)

    if (SubmitType === 'Error') {
      pending.reject(data)
    } else {
      if (data.Event === 'AccessToken') {
        this.markSessionReady()
      }
      pending.resolve(data)
    }
  }

  private async handleReconnectAccessTokenResp(resp: AccessTokenResp): Promise<void> {
    const [{ useAuthStore }, { default: router }] = await Promise.all([
      import('@/stores/auth'),
      import('@/router'),
    ])
    const auth = useAuthStore()
    const loginUser = resp.AccessTokenResp?.login ?? null
    if (loginUser === null) {
      auth.clearSession()
      this.sessionReadyQueue = []
      this._sessionEstablished = false
      void router.push('/login')
    } else if (auth.accessToken) {
      auth.setSession(auth.accessToken, loginUser)
      this.markSessionReady()
    } else {
      // Token was cleared between request and response (logout race). Discard.
      this.sessionReadyQueue = []
    }
  }

  private sendRaw(payload: NodeObject): void {
    if (import.meta.env.DEV) console.log('[socket] →', payload.Event, payload.Guid, payload.SubmitType)
    this.socket?.emit('NewObject', payload)
  }

  emit(event: string, payload: Record<string, unknown> = {}): string {
    const guid = crypto.randomUUID()
    this.sendRaw({ ...payload, Event: event, Guid: guid, SubmitType: 'Request' })
    return guid
  }

  request<T extends NodeObject = NodeObject>(
    event: string,
    payload: Record<string, unknown> = {},
    onAck?: AckCallback,
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const guid = crypto.randomUUID()
      const timer = window.setTimeout(() => {
        this.pending.delete(guid)
        reject(new Error(`The server didn't respond to ${event} within ${REQUEST_TIMEOUT_MS / 1000}s. The portal backend may be down — please try again in a few minutes.`))
      }, REQUEST_TIMEOUT_MS)

      this.pending.set(guid, {
        resolve: (value) => {
          clearTimeout(timer)
          ;(resolve as unknown as (v: NodeObject) => void)(value)
        },
        reject: (reason) => {
          clearTimeout(timer)
          reject(reason)
        },
        onAck,
      })
      this.sendRaw({ ...payload, Event: event, Guid: guid, SubmitType: 'Request' })
    })
  }

  // Returns an unsubscribe function.
  subscribe(_event: string, guid: string, callback: StreamCallback): () => void {
    const handlers = this.streams.get(guid) ?? []
    handlers.push(callback)
    this.streams.set(guid, handlers)
    return () => {
      const current = this.streams.get(guid) ?? []
      this.streams.set(guid, current.filter((h) => h !== callback))
    }
  }

  disconnect(): void {
    this.socket?.disconnect()
    this.socket = null
    this.hasConnectedOnce = false
    this._sessionEstablished = false
    this.sessionReadyQueue = []
    for (const [guid, pending] of this.pending) {
      pending.reject({ Event: 'disconnect', Guid: guid, SubmitType: 'Error', message: 'Socket disconnected' })
    }
    this.pending.clear()
    this.streams.clear()
  }

  get connected(): boolean {
    return this.socket?.connected ?? false
  }
}

export const socketService = new SocketService()

/**
 * Returns true only when the car is already awake or actively charging.
 * Any command that wakes the car MUST call this first — waking a car with a
 * depleted 12V battery can leave it stranded (car wakes, drains battery, won't start).
 */
export function isSafeToWakeCar(status: FullVehicleStatus2): boolean {
  return (
    status.vehicleStatus.engine ||
    status.vehicleStatus.acc ||
    (status.vehicleStatus.evStatus?.batteryCharge ?? false)
  )
}
